import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import LoadingState from "../Components/States/LoadingState";
import ConversationSidebar from "../Components/ConsultationsChat/ConversationSidebar";
import ActiveChatPanel from "../Components/ConsultationsChat/ActiveChatPanel";
import { useDoctorConversations } from "../hooks/useDoctorConversations";
import { useDoctorChat } from "../hooks/useDoctorChat";

const ChatPage = () => {
  const [searchParams] = useSearchParams();
  const doctorFromUrl = searchParams.get("doctor");
  const { conversations, loading, error, search, setSearch } = useDoctorConversations();
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const selectedDoctor = useMemo(
    () => conversations.find((c) => c.doctor._id === selectedConversationId)?.doctor ?? null,
    [conversations, selectedConversationId]
  );

  const chat = useDoctorChat(selectedConversationId);
  const displayMessages = chat.messages;

  useEffect(() => {
    if (loading || conversations.length === 0) return;

    if (doctorFromUrl && conversations.some((c) => c.doctor._id === doctorFromUrl)) {
      setSelectedConversationId(doctorFromUrl);
      return;
    }

    if (selectedConversationId) return;
    const mq = window.matchMedia("(min-width: 768px)");
    if (mq.matches) {
      setSelectedConversationId(conversations[0].doctor._id);
    }
  }, [loading, conversations, doctorFromUrl, selectedConversationId]);

  const showMobileChat = Boolean(selectedConversationId);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-6">
          {/* <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-on-primary-fixed-var">Doctor Consultations</h1>
            <p className="text-sm md:text-base text-on-surface-variant mt-1">
              Chat with Verified Care Team &amp; Specialists
            </p>
          </div> */}

          {loading && conversations.length === 0 ? (
            <LoadingState branded message="Loading conversations…" className="py-16" />
          ) : !loading && conversations.length === 0 ? (
            <div className="card p-8 text-center max-w-lg">
              <p className="text-on-surface-variant mb-4">
                Book a consultation to chat with your doctor here.
              </p>
              <Link to="/consultations" className="btn-primary inline-flex px-6 py-2.5 rounded-full text-sm font-semibold">
                Book a consultation
              </Link>
            </div>
          ) : (
            <div className="flex border border-surface-container rounded-2xl overflow-hidden bg-surface-lowest shadow-sm h-[calc(100vh-100px)] min-h-[500px] max-h-[780px] lg:h-[calc(100vh-260px)] lg:max-h-[820px]">
              <div
                className={`${
                  showMobileChat ? "hidden md:flex" : "flex"
                } w-full md:w-[340px] lg:w-[360px] shrink-0 flex-col min-h-0`}
              >
                <ConversationSidebar
                  conversations={conversations}
                  selectedConversationId={selectedConversationId}
                  onSelect={(doctor) => setSelectedConversationId(doctor._id)}
                  search={search}
                  onSearchChange={setSearch}
                  loading={loading}
                  error={error}
                />
              </div>

              <div
                className={`${
                  showMobileChat ? "flex" : "hidden md:flex"
                } flex-1 min-w-0 min-h-0 flex-col`}
              >
                {selectedDoctor ? (
                  <ActiveChatPanel
                    doctor={selectedDoctor}
                    showBack
                    onBack={() => setSelectedConversationId(null)}
                    chat={chat}
                    displayMessages={displayMessages}
                  />
                ) : (
                  <div className="flex flex-1 items-center justify-center bg-surface-low text-on-surface-variant text-sm p-8 text-center">
                    Select a conversation to view messages
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ChatPage;
